/**
 * seats.config.ts — single source of truth for the seat-aware front door.
 *
 * Every homepage component (SeatSelector, SeatPanel) and the signed-in
 * reference this file. A "seat" is a persona the front door speaks
 * to. A seat MAY map to a real login role (WorkspaceRole) and an existing
 * shell route, or it may be a marketing-only persona (adjuster, neutral)
 * with no login behind it — those are case VIEWS, not roles.
 *
 * Carrier is deliberately NOT a seat: carrier-facing frontends are on a
 * standing HOLD until a real carrier tenant exists. The only trace is the
 * soft CARRIER_NOTE line rendered in the footer — no signup CTA, no route.
 */

import type { WorkspaceRole } from './toolsManifest';

export type SeatId =
  | 'attorney'
  | 'accountant'
  | 'adjuster'
  | 'data_specialist'
  | 'neutral'
  | 'client';

export interface SeatCta {
  label: string;
  href: string;
}

export interface Seat {
  id: SeatId;
  /** Chip / eyebrow label, e.g. "Plaintiff attorney". */
  label: string;
  /** One line: who this seat is for. */
  tagline: string;
  /** The ONE job-to-be-done, rendered as the panel headline. */
  job: string;
  /** Exactly three concrete things the platform hands this seat. */
  capabilities: [string, string, string];
  /** The one credibility line, rendered set apart. */
  proof: string;
  /** Signed-out CTA. null = no self-serve signup (client is invited). */
  marketingCta: SeatCta | null;
  /** Quiet line rendered instead of a CTA when marketingCta is null. */
  ctaNote?: string;
  /** The real login role behind this seat, or null for view-only personas. */
  workspaceRole: WorkspaceRole | null;
  /**
   * SSOT (single source of truth) — the REAL current no-sidebar dashboard a
   * signed-in user of this seat lands on. USED at runtime: the homepage SeatPanel
   * deep-links here ("Open the workspace"), and signed-in landing in
   * `ConditionalHome` (App.tsx) MUST match this map — keep the two in sync.
   * `null` = the seat has no self-serve landing because its real surface is a
   * PER-CASE view reached with a case id (adjuster → `/adjuster/case/:id/analysis`
   * CaseWorkspaceShell; neutral → `/mediation/:reportId` MediationBoard); those
   * can't be deep-linked from the homepage without an id, so no link is shown.
   * Full seat→surface map + evidence: docs/architecture/SEAT_DASHBOARD_MAP.md.
   */
  dashboardRoute: string | null;
  /** false = soft persona: no live CTA, excluded from the selector. */
  live: boolean;
}

const ATTORNEY: Seat = {
  id: 'attorney',
  label: 'Plaintiff attorney',
  tagline: 'For plaintiff-side counsel building the business-interruption case.',
  job: 'Build a defensible case and a defensible demand.',
  capabilities: [
    'Elect the BI method — the sworn claim number N',
    'Generate the demand letter, sworn Proof of Loss, and court-ready PDF',
    'War Room: every argument grounded in the engines',
  ],
  proof: 'Every figure traces to an engine output and an attestation.',
  marketingCta: { label: 'Request early access', href: '/waitlist?seat=attorney' },
  workspaceRole: 'attorney',
  dashboardRoute: '/attorney/workdesk',
  live: true,
};

const ACCOUNTANT: Seat = {
  id: 'accountant',
  label: 'CPA / Accountant',
  tagline: 'For the forensic accountant who verifies and signs the numbers.',
  job: 'Verify and attest the numbers.',
  capabilities: [
    'Approve, dispute, or waive every engine',
    'See the forensic insights behind each figure',
    'Sign the sworn number — nothing exports until you approve',
  ],
  proof: 'The tightest human-in-the-loop checkpoint on the platform.',
  marketingCta: { label: 'Request early access', href: '/waitlist?seat=accountant' },
  // Internal CPA login role (external CPAs authenticate via JWT, see adjuster note).
  workspaceRole: 'accountant_internal',
  // Latest no-sidebar home: the internal accountant WorkDesk (Queue / My Cases /
  // Signed Off), which opens cases into CaseWorkspaceShell(role=accountant).
  dashboardRoute: '/workdesk/accountant-internal',
  live: true,
};

const ADJUSTER: Seat = {
  id: 'adjuster',
  label: 'Carrier adjuster',
  tagline: 'For the carrier side that has to hold a position on the same claim.',
  job: 'Hold an engine-grounded position.',
  capabilities: [
    'The carrier floor = N − C (mitigation capped at 30%)',
    'Consensus view across all three roles',
    'QA bot on every figure',
  ],
  proof: 'Same engine math both sides read — side-neutral.',
  // Persona only: the adjuster sees a per-case consensus VIEW, never a
  // login. live:true so the seat pitches; routing stays null by design.
  marketingCta: { label: 'Request a walkthrough', href: '/contact?seat=adjuster' },
  workspaceRole: null,
  dashboardRoute: null,
  live: true,
};

const DATA_SPECIALIST: Seat = {
  id: 'data_specialist',
  label: 'Data specialist',
  tagline: 'For the operations specialist who turns client paperwork into clean data.',
  job: 'Turn documents into clean data.',
  capabilities: [
    'Upload → OCR → classify → canonical fields',
    'The AI auto-requests exactly the missing docs',
    'Three agents asking for the same doc = one client request',
  ],
  proof: "You only chase what's actually missing.",
  marketingCta: { label: 'Request early access', href: '/waitlist?seat=data_specialist' },
  workspaceRole: 'data_specialist',
  dashboardRoute: '/workdesk/data-specialist',
  live: true,
};

const NEUTRAL: Seat = {
  id: 'neutral',
  label: 'Neutral / Mediator',
  tagline: 'For the mediator pricing the gap between the two numbers.',
  job: 'Find the number between.',
  capabilities: [
    'Expected value between the plaintiff number and the carrier floor',
    'Grounded in the same figures both sides already read',
    'Not a split-the-baby guess',
  ],
  proof: 'A defensible midpoint, computed.',
  // Real login role (D7: seat `neutral` <-> role `mediator`). Signed-in mediators land on
  // /mediation-wall (App.tsx roleLandingRoutes). dashboardRoute stays null on purpose: the
  // homepage "Open the workspace" deep-link is not offered for this seat (RoleRoute bounces
  // non-mediators to "/"). Nothing reads `workspaceRole` at runtime today.
  marketingCta: { label: 'Request a walkthrough', href: '/contact?seat=neutral' },
  workspaceRole: 'mediator',
  dashboardRoute: null,
  live: true,
};

const CLIENT: Seat = {
  id: 'client',
  label: 'Client / Policyholder',
  tagline: 'For the business owner whose claim this is.',
  job: 'Give us what is needed, once.',
  capabilities: [
    'Magic-link portal — no account to manage',
    'Upload documents and answer clarifications',
    'Only ever asked for what is missing',
  ],
  proof: 'Minutes, not a document scavenger hunt.',
  // Clients arrive by invitation (magic link → /portal/:token), not signup.
  // The CTA stays quiet on purpose.
  marketingCta: null,
  ctaNote: 'No signup needed — your attorney or CPA sends you a secure magic link.',
  workspaceRole: 'client',
  dashboardRoute: '/client',
  live: true,
};

export const SEATS: Seat[] = [
  ATTORNEY,
  ACCOUNTANT,
  ADJUSTER,
  DATA_SPECIALIST,
  NEUTRAL,
  CLIENT,
];

/** Seats shown in the homepage selector (carrier is excluded by design). */
export const LIVE_SEATS: Seat[] = SEATS.filter((seat) => seat.live);

export const DEFAULT_SEAT_ID: SeatId = 'attorney';

export function getSeat(id: SeatId): Seat {
  return LIVE_SEATS.find((seat) => seat.id === id) ?? ATTORNEY;
}

/**
 * Carrier audience entry (front door). The standing HOLD was lifted by the owner
 * 2026-10-06; carrier is now a first-class audience with a dedicated landing
 * (/for-carriers). It stays OUT of the 6-seat selector by design — carrier is not
 * a self-serve seat; firms are onboarded by platform staff who set
 * `law_firms.client_side`. This soft footer line is the front-door link to the
 * landing (no signup CTA in the selector).
 */
export const CARRIER_NOTE = {
  label: 'For carriers →',
  href: '/for-carriers',
} as const;
